// Popover API polyfill — only activates when native support is missing

var ToggleEvent = class extends Event {
  oldState;
  newState;
  constructor(type, { oldState = '', newState = '', ...init } = {}) {
    super(type, init);
    this.oldState = String(oldState || '');
    this.newState = String(newState || '');
  }
};

var toggleQueue = new WeakMap();
function queueToggle(el, oldState, newState) {
  toggleQueue.set(
    el,
    setTimeout(() => {
      if (!toggleQueue.has(el)) return;
      el.dispatchEvent(new ToggleEvent('toggle', { cancelable: false, oldState, newState }));
    }, 0),
  );
}

var ShadowRoot = globalThis.ShadowRoot || function () {};
var HTMLDialogElement = globalThis.HTMLDialogElement || function () {};
var topLayerElements = new WeakMap();
var autoPopoverList = new WeakMap();
var hintPopoverList = new WeakMap();
var visibilityState = new WeakMap();

function visState(pop) {
  return visibilityState.get(pop) || 'hidden';
}

var invokerMap = new WeakMap();

function lastSetElement(set) {
  return [...set].pop();
}

function activateTarget(el) {
  const pop = el.popoverTargetElement;
  if (!(pop instanceof HTMLElement)) return;
  const vis = visState(pop);
  if (el.popoverTargetAction === 'show' && vis === 'showing') return;
  if (el.popoverTargetAction === 'hide' && vis === 'hidden') return;
  if (vis === 'showing') {
    hidePopover(pop, true, true);
  } else if (isValid(pop, false)) {
    invokerMap.set(pop, el);
    showPopover(pop);
  }
}

function isValid(el, expectedToBeShowing) {
  if (el.popover !== 'auto' && el.popover !== 'manual' && el.popover !== 'hint') return false;
  if (!el.isConnected) return false;
  if (expectedToBeShowing && visState(el) !== 'showing') return false;
  if (!expectedToBeShowing && visState(el) !== 'hidden') return false;
  if (el instanceof HTMLDialogElement && el.hasAttribute('open')) return false;
  if (document.fullscreenElement === el) return false;
  return true;
}

function stackPos(pop) {
  if (!pop) return 0;
  const autos = autoPopoverList.get(document) || new Set();
  const hints = hintPopoverList.get(document) || new Set();
  if (hints.has(pop)) return [...hints].indexOf(pop) + autos.size + 1;
  if (autos.has(pop)) return [...autos].indexOf(pop) + 1;
  return 0;
}

function topClicked(target) {
  const clicked = nearestOpen(target);
  const invoked = nearestTarget(target);
  return stackPos(clicked) > stackPos(invoked) ? clicked : invoked;
}

function topAutoHint(doc) {
  const hints = hintPopoverList.get(doc) || new Set();
  const autos = autoPopoverList.get(doc) || new Set();
  const stack = hints.size > 0 ? hints : autos.size > 0 ? autos : null;
  if (stack) {
    const topPop = lastSetElement(stack);
    if (!topPop.isConnected) {
      stack.delete(topPop);
      return topAutoHint(doc);
    }
    return topPop;
  }
  return null;
}

function topInList(list) {
  for (const pop of list || []) {
    if (!pop.isConnected) list.delete(pop);
    else return pop;
  }
  return null;
}

function getRootNode(node) {
  if (typeof node.getRootNode === 'function') return node.getRootNode();
  if (node.parentNode) return getRootNode(node.parentNode);
  return node;
}

function nearestOpen(node) {
  while (node) {
    if (node instanceof HTMLElement && node.popover === 'auto' && visibilityState.get(node) === 'showing') return node;
    node = (node instanceof Element && node.assignedSlot) || node.parentElement || getRootNode(node);
    if (node instanceof ShadowRoot) node = node.host;
    if (node instanceof Document) return;
  }
}

function nearestTarget(node) {
  while (node) {
    const nodePop = node.popoverTargetElement;
    if (nodePop instanceof HTMLElement) return nodePop;
    node = node.parentElement || getRootNode(node);
    if (node instanceof ShadowRoot) node = node.host;
    if (node instanceof Document) return;
  }
}

function topAncestor(newPop, list) {
  const positions = new Map();
  let i = 0;
  for (const pop of list || []) {
    positions.set(pop, i);
    i += 1;
  }
  positions.set(newPop, i);
  i += 1;
  let result = null;
  function checkAnc(candidate) {
    if (!candidate) return;
    let okNesting = false,
      ancestor = null,
      pos = null;
    while (!okNesting) {
      ancestor = nearestOpen(candidate) || null;
      if (ancestor === null) return;
      if (!positions.has(ancestor)) return;
      if (newPop.popover === 'hint' || ancestor.popover === 'auto') okNesting = true;
      if (!okNesting) candidate = ancestor.parentElement;
    }
    pos = positions.get(ancestor);
    if (result === null || positions.get(result) < pos) result = ancestor;
  }
  checkAnc(newPop.parentElement || getRootNode(newPop));
  return result;
}

function isFocusable(ft) {
  if (ft.hidden || ft instanceof ShadowRoot) return false;
  if (
    ft instanceof HTMLButtonElement ||
    ft instanceof HTMLInputElement ||
    ft instanceof HTMLSelectElement ||
    ft instanceof HTMLTextAreaElement ||
    ft instanceof HTMLOptGroupElement ||
    ft instanceof HTMLOptionElement ||
    ft instanceof HTMLFieldSetElement
  ) {
    if (ft.disabled) return false;
  }
  if (ft instanceof HTMLInputElement && ft.type === 'hidden') return false;
  if (ft instanceof HTMLAnchorElement && ft.href === '') return false;
  return typeof ft.tabIndex === 'number' && ft.tabIndex !== -1;
}

function findFocus(ft) {
  if (ft.shadowRoot && ft.shadowRoot.delegatesFocus !== true) return null;
  let where = ft.shadowRoot || ft;
  let autoFocusDel = where.querySelector('[autofocus]');
  if (autoFocusDel) return autoFocusDel;
  const slots = where.querySelectorAll('slot');
  for (const slot of slots) {
    for (const assigned of slot.assignedElements({ flatten: true })) {
      if (assigned.hasAttribute('autofocus')) return assigned;
      autoFocusDel = assigned.querySelector('[autofocus]');
      if (autoFocusDel) return autoFocusDel;
    }
  }
  const walker = ft.ownerDocument.createTreeWalker(where, NodeFilter.SHOW_ELEMENT);
  let desc = walker.currentNode;
  while (desc) {
    if (isFocusable(desc)) return desc;
    desc = walker.nextNode();
  }
}

function focusPopover(subject) {
  findFocus(subject)?.focus();
}

var prevFocused = new WeakMap();

function showPopover(el) {
  if (!isValid(el, false)) return;
  const doc = el.ownerDocument;
  if (!el.dispatchEvent(new ToggleEvent('beforetoggle', { cancelable: true, oldState: 'closed', newState: 'open' })))
    return;
  if (!isValid(el, false)) return;
  let shouldRestoreFocus = false;
  const origType = el.popover;
  let stackToAppend = null;
  const autoAnc = topAncestor(el, autoPopoverList.get(doc) || new Set());
  const hintAnc = topAncestor(el, hintPopoverList.get(doc) || new Set());
  if (origType === 'auto') {
    closeAllInList(hintPopoverList.get(doc) || new Set(), shouldRestoreFocus, true);
    hideUntil(autoAnc || doc, shouldRestoreFocus, true);
    stackToAppend = 'auto';
  }
  if (origType === 'hint') {
    if (hintAnc) {
      hideUntil(hintAnc, shouldRestoreFocus, true);
      stackToAppend = 'hint';
    } else {
      closeAllInList(hintPopoverList.get(doc) || new Set(), shouldRestoreFocus, true);
      if (autoAnc) {
        hideUntil(autoAnc, shouldRestoreFocus, true);
        stackToAppend = 'auto';
      } else {
        stackToAppend = 'hint';
      }
    }
  }
  if (origType === 'auto' || origType === 'hint') {
    if (origType !== el.popover || !isValid(el, false)) return;
    if (!topAutoHint(doc)) shouldRestoreFocus = true;
    if (stackToAppend === 'auto') {
      if (!autoPopoverList.has(doc)) autoPopoverList.set(doc, new Set());
      autoPopoverList.get(doc).add(el);
    } else if (stackToAppend === 'hint') {
      if (!hintPopoverList.has(doc)) hintPopoverList.set(doc, new Set());
      hintPopoverList.get(doc).add(el);
    }
  }
  prevFocused.delete(el);
  const origFocused = doc.activeElement;
  el.classList.add(':popover-open');
  visibilityState.set(el, 'showing');
  if (!topLayerElements.has(doc)) topLayerElements.set(doc, new Set());
  topLayerElements.get(doc).add(el);
  setExpanded(invokerMap.get(el), true);
  focusPopover(el);
  if (shouldRestoreFocus && origFocused && el.popover === 'auto') {
    prevFocused.set(el, origFocused);
  }
  queueToggle(el, 'closed', 'open');
}

function hidePopover(el, focusPrev = false, fire = false) {
  if (!isValid(el, true)) return;
  const doc = el.ownerDocument;
  if (['auto', 'hint'].includes(el.popover)) {
    hideUntil(el, focusPrev, fire);
    if (!isValid(el, true)) return;
  }
  const autoList = autoPopoverList.get(doc) || new Set();
  const autoContains = autoList.has(el) && lastSetElement(autoList) === el;
  setExpanded(invokerMap.get(el), false);
  invokerMap.delete(el);
  if (fire) {
    el.dispatchEvent(new ToggleEvent('beforetoggle', { oldState: 'open', newState: 'closed' }));
    if (autoContains && lastSetElement(autoList) !== el) {
      hideUntil(el, focusPrev, fire);
    }
    if (!isValid(el, true)) return;
  }
  topLayerElements.get(doc)?.delete(el);
  autoList.delete(el);
  hintPopoverList.get(doc)?.delete(el);
  el.classList.remove(':popover-open');
  visibilityState.set(el, 'hidden');
  if (fire) queueToggle(el, 'open', 'closed');
  const prev = prevFocused.get(el);
  if (prev) {
    prevFocused.delete(el);
    if (focusPrev) prev.focus();
  }
}

function closeAll(doc, focusPrev = false, fire = false) {
  let pop = topAutoHint(doc);
  while (pop) {
    hidePopover(pop, focusPrev, fire);
    pop = topAutoHint(doc);
  }
}

function closeAllInList(list, focusPrev = false, fire = false) {
  let pop = topInList(list);
  while (pop) {
    hidePopover(pop, focusPrev, fire);
    pop = topInList(list);
  }
}

function hideStackTo(endpoint, set, focusPrev, fire) {
  let repeating = false,
    hasRun = false;
  while (repeating || !hasRun) {
    hasRun = true;
    let lastToHide = null,
      foundEnd = false;
    for (const pop of set) {
      if (pop === endpoint) foundEnd = true;
      else if (foundEnd) {
        lastToHide = pop;
        break;
      }
    }
    if (!lastToHide) return;
    while (visState(lastToHide) === 'showing' && set.size) {
      hidePopover(lastSetElement(set), focusPrev, fire);
    }
    if (set.has(endpoint) && lastSetElement(set) !== endpoint) repeating = true;
    if (repeating) fire = false;
  }
}

function hideUntil(endpoint, focusPrev, fire) {
  const doc = endpoint.ownerDocument || endpoint;
  if (endpoint instanceof Document) return closeAll(doc, focusPrev, fire);
  if (hintPopoverList.get(doc)?.has(endpoint)) {
    hideStackTo(endpoint, hintPopoverList.get(doc), focusPrev, fire);
    return;
  }
  closeAllInList(hintPopoverList.get(doc) || new Set(), focusPrev, fire);
  if (!autoPopoverList.get(doc)?.has(endpoint)) return;
  hideStackTo(endpoint, autoPopoverList.get(doc), focusPrev, fire);
}

var ptrTargets = new WeakMap();
function lightDismiss(event) {
  if (!event.isTrusted) return;
  const target = event.composedPath()[0];
  if (!target) return;
  const doc = target.ownerDocument;
  const topPop = topAutoHint(doc);
  if (!topPop) return;
  const anc = topClicked(target);
  if (anc && event.type === 'pointerdown') {
    ptrTargets.set(doc, anc);
  } else if (event.type === 'pointerup') {
    const same = ptrTargets.get(doc) === anc;
    ptrTargets.delete(doc);
    if (same) hideUntil(anc || doc, false, true);
  }
}

var initExpanded = new WeakMap();
function setExpanded(el, force = false) {
  if (!el) return;
  if (!initExpanded.has(el)) initExpanded.set(el, el.getAttribute('aria-expanded'));
  const pop = el.popoverTargetElement;
  if (pop instanceof HTMLElement && pop.popover === 'auto') {
    el.setAttribute('aria-expanded', String(force));
  } else {
    const init = initExpanded.get(el);
    if (!init) el.removeAttribute('aria-expanded');
    else el.setAttribute('aria-expanded', init);
  }
}

// Main application
var ShadowRoot2 = globalThis.ShadowRoot || function () {};

function isSupported() {
  return (
    typeof HTMLElement !== 'undefined' &&
    typeof HTMLElement.prototype === 'object' &&
    'popover' in HTMLElement.prototype
  );
}

function patchSelectorFn(obj, name, mapper) {
  const orig = obj[name];
  Object.defineProperty(obj, name, {
    value(selector) {
      return orig.call(this, mapper(selector));
    },
  });
}

var popoverRe = /(^|[^\\]):popover-open\b/g;

function hasLayerSupport() {
  return typeof globalThis.CSSLayerBlockRule === 'function';
}

function getStyles() {
  const useLayer = hasLayerSupport();
  return `
${useLayer ? '@layer popover-polyfill {' : ''}
  :where([popover]) {
    position: fixed;
    z-index: 2147483647;
    inset: 0;
    padding: 0.25em;
    width: fit-content;
    height: fit-content;
    border-width: initial;
    border-color: initial;
    border-image: initial;
    border-style: solid;
    background-color: canvas;
    color: canvastext;
    overflow: auto;
    margin: auto;
  }

  :where([popover]:not(.\\:popover-open)) {
    display: none;
  }

  :where(dialog[popover].\\:popover-open) {
    display: block;
  }

  :where(dialog[popover][open]) {
    display: revert;
  }

  :where([anchor].\\:popover-open) {
    inset: auto;
  }

  :where([anchor]:popover-open) {
    inset: auto;
  }

  @supports not (background-color: canvas) {
    :where([popover]) {
      background-color: white;
      color: black;
    }
  }

  @supports (width: -moz-fit-content) {
    :where([popover]) {
      width: -moz-fit-content;
      height: -moz-fit-content;
    }
  }

  @supports not (inset: 0) {
    :where([popover]) {
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    }
  }
${useLayer ? '}' : ''}
`;
}

var popoverStyleSheet = null;
function injectStyles(root) {
  const styles = getStyles();
  if (popoverStyleSheet === null) {
    try {
      popoverStyleSheet = new CSSStyleSheet();
      popoverStyleSheet.replaceSync(styles);
    } catch {
      popoverStyleSheet = false;
    }
  }
  if (popoverStyleSheet === false) {
    const sheet = document.createElement('style');
    sheet.textContent = styles;
    if (root instanceof Document) root.head.prepend(sheet);
    else root.prepend(sheet);
  } else {
    root.adoptedStyleSheets = [popoverStyleSheet, ...root.adoptedStyleSheets];
  }
}

function apply() {
  if (typeof window === 'undefined') return;
  window.ToggleEvent = window.ToggleEvent || ToggleEvent;
  function rewriteSelector(selector) {
    if (selector?.includes(':popover-open')) {
      selector = selector.replace(popoverRe, '$1.\\:popover-open');
    }
    return selector;
  }
  patchSelectorFn(Document.prototype, 'querySelector', rewriteSelector);
  patchSelectorFn(Document.prototype, 'querySelectorAll', rewriteSelector);
  patchSelectorFn(Element.prototype, 'querySelector', rewriteSelector);
  patchSelectorFn(Element.prototype, 'querySelectorAll', rewriteSelector);
  patchSelectorFn(Element.prototype, 'matches', rewriteSelector);
  patchSelectorFn(Element.prototype, 'closest', rewriteSelector);
  patchSelectorFn(DocumentFragment.prototype, 'querySelectorAll', rewriteSelector);
  Object.defineProperties(HTMLElement.prototype, {
    popover: {
      enumerable: true,
      configurable: true,
      get() {
        if (!this.hasAttribute('popover')) return null;
        const value = (this.getAttribute('popover') || '').toLowerCase();
        if (value === '' || value == 'auto') return 'auto';
        if (value == 'hint') return 'hint';
        return 'manual';
      },
      set(value) {
        if (value === null) this.removeAttribute('popover');
        else this.setAttribute('popover', value);
      },
    },
    showPopover: {
      enumerable: true,
      configurable: true,
      value(options = {}) {
        showPopover(this);
      },
    },
    hidePopover: {
      enumerable: true,
      configurable: true,
      value() {
        hidePopover(this, true, true);
      },
    },
    togglePopover: {
      enumerable: true,
      configurable: true,
      value(options = {}) {
        if (typeof options === 'boolean') options = { force: options };
        if ((visibilityState.get(this) === 'showing' && options.force === void 0) || options.force === false) {
          hidePopover(this, true, true);
        } else if (options.force === void 0 || options.force === true) {
          showPopover(this);
        }
        return visibilityState.get(this) === 'showing';
      },
    },
  });
  const origShadow = Element.prototype.attachShadow;
  if (origShadow) {
    Object.defineProperties(Element.prototype, {
      attachShadow: {
        enumerable: true,
        configurable: true,
        writable: true,
        value(options) {
          const sr = origShadow.call(this, options);
          injectStyles(sr);
          return sr;
        },
      },
    });
  }
  const origInternals = HTMLElement.prototype.attachInternals;
  if (origInternals) {
    Object.defineProperties(HTMLElement.prototype, {
      attachInternals: {
        enumerable: true,
        configurable: true,
        writable: true,
        value() {
          const internals = origInternals.call(this);
          if (internals.shadowRoot) injectStyles(internals.shadowRoot);
          return internals;
        },
      },
    });
  }
  var targetAssoc = new WeakMap();
  function mixinInvoker(Cls) {
    Object.defineProperties(Cls.prototype, {
      popoverTargetElement: {
        enumerable: true,
        configurable: true,
        set(targetEl) {
          if (targetEl === null) {
            this.removeAttribute('popovertarget');
            targetAssoc.delete(this);
          } else if (!(targetEl instanceof Element)) {
            throw new TypeError(`popoverTargetElement must be an element or null`);
          } else {
            this.setAttribute('popovertarget', '');
            targetAssoc.set(this, targetEl);
          }
        },
        get() {
          if (this.localName !== 'button' && this.localName !== 'input') return null;
          if (this.localName === 'input' && this.type !== 'reset' && this.type !== 'image' && this.type !== 'button')
            return null;
          if (this.disabled) return null;
          if (this.form && this.type === 'submit') return null;
          const targetEl = targetAssoc.get(this);
          if (targetEl && targetEl.isConnected) return targetEl;
          if (targetEl && !targetEl.isConnected) {
            targetAssoc.delete(this);
            return null;
          }
          const root = getRootNode(this);
          const idref = this.getAttribute('popovertarget');
          if ((root instanceof Document || root instanceof ShadowRoot2) && idref) {
            return root.getElementById(idref) || null;
          }
          return null;
        },
      },
      popoverTargetAction: {
        enumerable: true,
        configurable: true,
        get() {
          const value = (this.getAttribute('popovertargetaction') || '').toLowerCase();
          if (value === 'show' || value === 'hide') return value;
          return 'toggle';
        },
        set(value) {
          this.setAttribute('popovertargetaction', value);
        },
      },
    });
  }
  mixinInvoker(HTMLButtonElement);
  mixinInvoker(HTMLInputElement);
  const onInvoker = (event) => {
    if (event.defaultPrevented) return;
    const path = event.composedPath();
    const target = path[0];
    if (!(target instanceof Element) || target?.shadowRoot) return;
    const root = getRootNode(target);
    if (!(root instanceof ShadowRoot2 || root instanceof Document)) return;
    const inv = path.find((el) => el.matches?.call(el, '[popovertargetaction],[popovertarget]'));
    if (inv) {
      activateTarget(inv);
      event.preventDefault();
    }
  };
  const onKeydown = (event) => {
    const { key, target } = event;
    if (!event.defaultPrevented && target && (key === 'Escape' || key === 'Esc')) {
      hideUntil(target.ownerDocument, true, true);
    }
  };
  const addListeners = (root) => {
    root.addEventListener('click', onInvoker);
    root.addEventListener('keydown', onKeydown);
    root.addEventListener('pointerdown', lightDismiss);
    root.addEventListener('pointerup', lightDismiss);
  };
  addListeners(document);
  injectStyles(document);
}

if (!isSupported()) apply();
